import IntlMessages from 'components/utility/IntlMessages';
import links from 'helpers/links';
import { Map } from 'immutable';
import { pathToRegexp } from 'path-to-regexp';
import { ACTIONS } from 'react-joyride';
import storeObject from 'redux/storeDeclaration';
import actions from './actions';

//ATS joyride docs @ https://wiki.poslovi.live/en/ats/joyride

const getSpotlightStyles = () => {
  const width = window.innerWidth;
  if (width <= 1441) {
    return {
      minWidth: 254,
    };
  }
  if (width > 1441 && width <= 1538) {
    return {
      minWidth: 54,
    };
  }

  return {};
};

export const tourHelpStepTextID = 'tour.help.step';

const cancelSteps = () => [
  {
    disableBeacon: true,
    target: '.tour-help-button-step',
    content: <IntlMessages id={tourHelpStepTextID} />,
    spotlightClicks: false,
    placement: 'right',
    styles: {
      spotlight: getSpotlightStyles(),
    },
  },
];

const cancelTour = () => ({
  callback: ({ action }) => {
    if (action === ACTIONS.CLOSE || action === ACTIONS.NEXT) {
      storeObject.getStore().dispatch({
        type: actions.RUN_CANCEL_TOUR_CLOSE,
      });
    }
  },
  steps: cancelSteps(),
  run: false,
  continuous: true,
  stepIndex: 0,
});

const initState = new Map({
  run: false,
  params: {},
  stepIndex: 0,
  sectionsParams: new Map({}),
  finished: new Map({}),
  checkpointFlags: new Map({}),
  checkpointNextStep: new Map({}),
  checkpointSeen: new Map({}),
  reservedSection: null,
  sections: [],
});

export default function tourReducer(state = initState, action) {
  switch (action.type) {
    case actions.TOUR_ADD_SECTION:
      return state.set(
        'sections',
        state.get('sections').includes(action.payload)
          ? state.get('sections')
          : [...state.get('sections'), action.payload]
      );

    case actions.TOUR_SET_SECTION_PARAMS:
      return state.setIn(['sectionsParams', action.payload.section], action.payload.params);

    case actions.VERIFY_CHECKPOINT_DONE: {
      // Record where to resume even when another tour is on screen and this section can't start
      // yet — it still needs the answer when its turn comes.
      const withNextStep = state.setIn(
        ['checkpointNextStep', action.payload.section],
        action.payload.nextStep ?? null
      );

      const reservedSection = state.get('reservedSection');
      if (state.get('run') || (reservedSection && reservedSection !== action.payload.section)) {
        return withNextStep;
      }

      return withNextStep.withMutations((newState) => {
        return newState
          .set('params', state.getIn(['sectionsParams', action.payload.section]))
          .set('run', action.payload.state)
          .setIn(['finished', action.payload.section], !action.payload.state);
      });
    }

    case actions.RUN_CANCEL_TOUR_CLOSE:
      return state.set('run', false);

    case actions.RUN_CANCEL_TOUR:
      return state.withMutations((newState) => {
        return newState
          .set('params', cancelTour())
          .set('stepIndex', 0)
          .set('run', action.payload.state === actions.RUN);
      });

    case actions.TOUR_RUN_SECTION:
      return state.withMutations((newState) => {
        return newState
          .set('params', state.getIn(['sectionsParams', action.payload.section]))
          .set('run', action.payload.state === actions.RUN)
          .setIn(['finished', action.payload.section], action.payload.state !== actions.RUN);
      });

    case actions.TOUR_SET_INDEX:
      return state.set('stepIndex', action.payload);

    case actions.TOUR_SET_PARAMS:
      return state.set('params', action.payload);

    case actions.TOUR_REMOVE_SECTION:
      return state.withMutations((newState) => {
        return newState
          .set(
            'sections',
            state.get('sections').filter((item) => item !== action.payload)
          )
          .set(
            'sectionsParams',
            state.get('sectionsParams').filter((item, key) => key !== action.payload)
          );
      });
    case actions.TOUR_REMOVE_FINISHED:
      return state.deleteIn(['finished', action.payload]);
    case actions.VERIFY_CHECKPOINT_FLAG_DONE:
      return state.withMutations((newState) => {
        return newState
          .setIn(['checkpointFlags', action.payload.section], true)
          .setIn(['checkpointNextStep', action.payload.section], action.payload.nextStep ?? null);
      });
    case actions.CLEAR_CHECKPOINT_FLAG:
      return state.deleteIn(['checkpointFlags', action.payload]);
    case actions.MARK_CHECKPOINT_SEEN:
      return state.setIn(['checkpointSeen', action.payload], true);
    case actions.SET_RESERVED_SECTION:
      if (action.payload.reserved) return state.set('reservedSection', action.payload.section);
      return state.get('reservedSection') === action.payload.section
        ? state.set('reservedSection', null)
        : state;
    case actions.SET_CHECKPOINT_NEXT_STEP:
      return state.setIn(['checkpointNextStep', action.payload.section], action.payload.nextStep);
    case '@@router/LOCATION_CHANGE': {
      const matchApplications = pathToRegexp(links.application().singleApplicationPattern).exec(
        action.payload.location.pathname
      );
      const matchProject = pathToRegexp(links.project().pattern).exec(
        action.payload.location.pathname
      );
      const matchSelection = pathToRegexp(links.selection().pattern).exec(
        action.payload.location.pathname
      );
      if (matchSelection || matchApplications || matchProject) {
        return state;
      }
      return initState;
    }
    case actions.TOUR_RESET:
    case 'LOGOUT':
      return initState;
    default:
      return state;
  }
}
